import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Alert,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Fab,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Slide,
  Stack,
  Typography,
} from "@mui/material";
import React, { memo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import FilesUploader from "../../components/FilesUploader";
import ServiceCard from "../../components/ServiceCard";
import ButtonControl from "../../components/controls/ButtonControl";
import InputControl from "../../components/controls/InputControl";
import RadioGroupControl from "../../components/controls/RadioGroupControl";
import SelectControl from "../../components/controls/SelectControl";
import { API_QUOTES } from "../../context/ApiEndPoints";
import useAppContext from "../../hooks/useAppContext";
import FormCustomerInfo from "./FormCustomerInfo";

const objRecord = {
  industry: "Advertising",
  necessaryInfo_: "",
  email: "",
  firstName: "",
  lastName: "",
  address: "",
  city: "",
  state: "",
  country: "",
  zipCode: "",
  phoneNumber: "",
  company: "",
  taxNumber: "",
  whereFindUs: "",
};

function FormAdvertising({ quoteId }) {
  const { user } = useAppContext();
  const navigate = useNavigate();

  const [values, setValues] = useState({ ...objRecord });
  const [errors, setErrors] = useState({});
  const [responseId, setResponseId] = useState();
  const [isSaving, setIsSaving] = useState(false);
  const [fetchError, setFetchError] = useState(false);

  const validate = (input = values) => {
    let temp = { ...errors };

    // Customer Info validation
    if ("email" in input) {
      temp.email = /^[A-Z0-9._%+-]+@([A-Z0-9-]+\.)+[A-Z]{2,4}$/i.test(
        input.email
      )
        ? ""
        : "Email in not valid.";
      temp.email = input.email ? temp.email : "Email is required.";
    }
    if ("firstName" in input)
      temp.firstName = input.firstName ? "" : "This field is required.";
    if ("lastName" in input)
      temp.lastName = input.lastName ? "" : "This field is required.";
    if ("city" in input)
      temp.city = input.city ? "" : "This field is required.";
    if ("state" in input)
      temp.state = input.state ? "" : "This field is required.";
    if ("country" in input)
      temp.country = input.country ? "" : "This field is required.";
    if ("zipCode" in input)
      temp.zipCode = input.zipCode ? "" : "This field is required.";
    if ("phoneNumber" in input)
      temp.phoneNumber = input.phoneNumber ? "" : "This field is required.";

    setErrors({ ...temp });
    if (input === values) return Object.values(temp).every((x) => x === "");
  };

  const handleInputChange = (e, switchName = null) => {
    const { name, value } = e.target;
    values[name] = value;
    if (switchName) values[switchName] = true;

    setValues({ ...values });
    validate({ [name]: value });
  };

  const [step, setStep] = useState(0);
  const [items, setItems] = useState([]);
  const nextStep = () => {
    // ARRAY: create selected services array;
    const x = Object.keys(values).filter((k) => k.startsWith("_") && values[k]);
    setItems(x);
    setStep(step + 1);
  };

  // Go back to prev step
  const prevStep = () => {
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validate()) {
      setIsSaving(true);

      const data = {};
      const quoteServices = {};
      const quoteItems = {};
      const quoteInfo = {};
      for (const [key, val] of Object.entries(values)) {
        if (key.startsWith("_") && val) {
          const k = key.slice(1);
          quoteServices[k] = val;
          continue;
        }

        if (key.endsWith("_") && val) {
          const a = key.slice(0, -1);
          const b = a.replace(/([A-Z])/g, " $1").trim();
          const k = b.replace(/^\w/, (c) => c.toUpperCase());
          quoteInfo[k] = val;
          continue;
        }

        if (/^[A-Z]/.test(key[0]) && val) {
          quoteItems[key] = typeof val === "boolean" ? "Yes" : val;
          continue;
        }

        if (/^[a-z]/.test(key[0])) {
          data[key] = val;
          continue;
        }
      }

      for (const [key] of Object.entries(data)) {
        if (key.endsWith("_") || key.startsWith("_")) {
          delete data[key];
        }
      }

      let record = data;
      record.quoteServices = JSON.stringify(quoteServices);
      record.quoteItems = JSON.stringify(quoteItems);
      record.quoteInfo = JSON.stringify(quoteInfo);

      // Create formData
      const formData = new FormData();
      // Add quote values to formData
      for (const [key, val] of Object.entries(record)) {
        formData.append(key, val);
      }

      // Add fleetMarketingFile values to formData
      if (values.fleetMarketingFile) {
        for (const val of Object.values(values.fleetMarketingFile)) {
          formData.append("fleetMarketingFile", val);
        }
      }

      // Add windowGraphicsFile values to formData
      if (values.windowGraphicsFile) {
        for (const val of Object.values(values.windowGraphicsFile)) {
          formData.append("windowGraphicsFile", val);
        }
      }

      // Add printServiceFile values to formData
      if (values.printServiceFile) {
        for (const val of Object.values(values.printServiceFile)) {
          formData.append("printServiceFile", val);
        }
      }

      // Add SignsFile values to formData
      if (values.SignsFile) {
        for (const val of Object.values(values.SignsFile)) {
          formData.append("SignsFile", val);
        }
      }

      // Add flagsAndBannersFile values to formData
      if (values.flagsAndBannersFile) {
        for (const val of Object.values(values.flagsAndBannersFile)) {
          formData.append("flagsAndBannersFile", val);
        }
      }

      // Add exhibitStandsFile values to formData
      if (values.exhibitStandsFile) {
        for (const val of Object.values(values.exhibitStandsFile)) {
          formData.append("exhibitStandsFile", val);
        }
      }

      // Add infoFiles values to formData
      if (values.infoFile) {
        for (const val of Object.values(values.infoFile)) {
          formData.append("infoFile", val);
        }
      }

      // POST request using fetch
      const requestOptions = {
        method: "POST",
        headers: { Authorization: "Bearer " + user.token },
        body: formData,
      };

      try {
        const response = await fetch(API_QUOTES, requestOptions);
        const isJson = response.headers
          .get("content-type")
          ?.includes("application/json");
        const json = isJson && (await response.json());

        // check for error response
        if (!response.ok) {
          // get error message from body or default to response status
          const error = (json && json.message) || response.status;
          throw Error(error);
        }

        setStep(step + 1);
        setValues({ ...objRecord });
        setErrors({});
        setResponseId(json);
      } catch (error) {
        setFetchError(
          <Alert severity="error">
            {!error.message ? error : error.message}
          </Alert>
        );
      } finally {
        setIsSaving(false);
      }
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Slide
          direction="right"
          in={step === 0 ? true : false}
          style={{ display: step === 0 ? "flex" : "none" }}
        >
          <Grid container>
            <Grid item xs={12} md={9}>
              <Grid container spacing={1} align="left">
                <Grid item xs={12} md={6}>
                  <ServiceCard
                    services={[
                      "FleetMarketingOption",
                      "FleetMarketingFile",
                      "fleetMarketingNumberOfVehicles",
                      "FleetMarketingVehicleBrand",
                      "FleetMarketingVehicleYear",
                      "FleetMarketingVehicleColor",
                    ]}
                    values={values}
                    setValues={setValues}
                    switchName="_FleetMarketing"
                    icon="wi-fleet-marketing"
                    title="Fleet Marketing"
                  >
                    <RadioGroupControl
                      aria-label="fleet marketing"
                      name="FleetMarketingOption"
                      onChange={(e) => handleInputChange(e, "_FleetMarketing")}
                      options={[
                        {
                          label: "Design Request",
                          value: "Design Request",
                        },
                        {
                          label: "I Have a Design",
                          value: "I Have a Design",
                        },
                      ]}
                    />
                    <FormControl margin="normal" fullWidth>
                      <InputLabel>Upload Design / Example</InputLabel>
                      <FilesUploader
                        inputName="fleetMarketingFile"
                        accept="image/*"
                        values={values}
                        setValues={setValues}
                      />
                    </FormControl>
                    <InputControl
                      type="number"
                      label="Number Of Vehicles"
                      name="FleetMarketingNumberOfVehicles"
                      onChange={(e) => handleInputChange(e, "_FleetMarketing")}
                    />
                    <InputControl
                      label="Vehicle Brand"
                      name="FleetMarketingVehicleBrand"
                      onChange={(e) => handleInputChange(e, "_FleetMarketing")}
                    />
                    <InputControl
                      label="Vehicle Type"
                      name="FleetMarketingVehicleType"
                      onChange={(e) => handleInputChange(e, "_FleetMarketing")}
                    />
                    <InputControl
                      label="Vehicle Year"
                      name="FleetMarketingVehicleYear"
                      onChange={(e) => handleInputChange(e, "_FleetMarketing")}
                    />
                    <InputControl
                      label="Vehicle Color"
                      name="FleetMarketingVehicleColor"
                      onChange={(e) => handleInputChange(e, "_FleetMarketing")}
                    />
                  </ServiceCard>
                </Grid>
                <Grid item xs={12} md={6}>
                  <ServiceCard
                    services={[
                      "WindowGraphicsOption",
                      "windowGraphicsFile",
                      "WindowGraphicsQuantity",
                      "WindowGraphicsHeight",
                      "WindowGraphicsWidth",
                      "WindowGraphicsUnits",
                      "WindowGraphicsMoreInfo",
                    ]}
                    values={values}
                    setValues={setValues}
                    switchName="_WindowGraphics"
                    icon="wi-window-graphics"
                    title="Window Graphics"
                  >
                    <RadioGroupControl
                      aria-label="window graphics"
                      name="WindowGraphicsOption"
                      onChange={(e) => handleInputChange(e, "_WindowGraphics")}
                      options={[
                        {
                          label: "Design Request",
                          value: "Design Request",
                        },
                        {
                          label: "I Have a Design",
                          value: "I Have a Design",
                        },
                      ]}
                    />
                    <FormControl margin="normal" fullWidth>
                      <InputLabel>Upload Design / Example</InputLabel>
                      <FilesUploader
                        inputName="windowGraphicsFile"
                        accept="image/*"
                        values={values}
                        setValues={setValues}
                      />
                    </FormControl>
                    <InputControl
                      type="number"
                      label="Quantity"
                      name="WindowGraphicsQuantity"
                      onChange={(e) => handleInputChange(e, "_WindowGraphics")}
                    />
                    <Stack direction="row">
                      <InputControl
                        type="number"
                        label="Dimension Height"
                        name="WindowGraphicsHeight"
                        onChange={(e) =>
                          handleInputChange(e, "_WindowGraphics")
                        }
                      />
                      <InputControl
                        type="number"
                        label="Dimension Width"
                        name="WindowGraphicsWidth"
                        onChange={(e) => {
                          handleInputChange(e, "_WindowGraphics");
                          if (!values.WindowGraphicsWidth)
                            setValues({ ...values, WindowGraphicsUnits: "" });
                        }}
                      />
                      {values.WindowGraphicsWidth && (
                        <SelectControl
                          label="Units"
                          name="WindowGraphicsUnits"
                          value={
                            values.WindowGraphicsUnits
                              ? values.WindowGraphicsUnits
                              : ""
                          }
                          onChange={(e) =>
                            handleInputChange(e, "_WindowGraphics")
                          }
                          options={[
                            { label: "cm", value: "cm" },
                            { label: "Inch", value: "Inch" },
                          ]}
                        />
                      )}
                    </Stack>
                    <InputControl
                      label="Tell me more"
                      name="WindowGraphicsMoreInfo"
                      multiline
                      rows={3}
                      onChange={(e) => handleInputChange(e, "_WindowGraphics")}
                    />
                  </ServiceCard>
                </Grid>
                <Grid item xs={12} md={6}>
                  <ServiceCard
                    services={["GraphicDesignOption", "GraphicDesignMoreInfo"]}
                    values={values}
                    setValues={setValues}
                    switchName="_GraphicDesign"
                    icon="wi-graphic-design"
                    title="Graphic Design"
                  >
                    <RadioGroupControl
                      aria-label="graphic graphics"
                      name="GraphicDesignOption"
                      onChange={(e) => handleInputChange(e, "_GraphicDesign")}
                      options={[
                        {
                          label: "Logo",
                          value: "Logo",
                        },
                        {
                          label: "Brandingbook",
                          value: "Brandingbook",
                        },
                        {
                          label: "Flyer",
                          value: "Flyer",
                        },
                        {
                          label: "Fleetmarketing",
                          value: "Fleetmarketing",
                        },
                        {
                          label: "Business cards",
                          value: "Business cards",
                        },
                        {
                          label: "Signs",
                          value: "Signs",
                        },
                        {
                          label: "Others",
                          value: "Others",
                        },
                      ]}
                    />
                    <InputControl
                      label="Tell Me More"
                      name="GraphicDesignMoreInfo"
                      multiline
                      rows={3}
                      onChange={(e) => handleInputChange(e, "_GraphicDesign")}
                    />
                  </ServiceCard>
                </Grid>
                <Grid item xs={12} md={6}>
                  <ServiceCard
                    services={[
                      "PrintServiceOption",
                      "printServiceFile",
                      "PrintServiceQuantity",
                      "PrintServiceHeight",
                      "PrintServiceWidth",
                      "PrintServiceUnits",
                      "PrintServiceMoreInfo",
                    ]}
                    values={values}
                    setValues={setValues}
                    switchName="_PrintService"
                    icon="wi-print-service"
                    title="Print Service"
                  >
                    <RadioGroupControl
                      aria-label="print service"
                      name="PrintServiceOption"
                      onChange={(e) => handleInputChange(e, "_PrintService")}
                      options={[
                        {
                          label: "Design Request",
                          value: "Design Request",
                        },
                        {
                          label: "I Have a Design",
                          value: "I Have a Design",
                        },
                      ]}
                    />
                    <FormControl margin="normal" fullWidth>
                      <InputLabel>Upload Design / Example</InputLabel>
                      <FilesUploader
                        inputName="printServiceFile"
                        accept="image/*"
                        values={values}
                        setValues={setValues}
                      />
                    </FormControl>
                    <InputControl
                      type="number"
                      label="Quantity"
                      name="PrintServiceQuantity"
                      onChange={(e) => handleInputChange(e, "_PrintService")}
                    />
                    <Stack direction="row">
                      <InputControl
                        type="number"
                        label="Dimension Height"
                        name="PrintServiceHeight"
                        onChange={(e) => handleInputChange(e, "_PrintService")}
                      />
                      <InputControl
                        type="number"
                        label="Dimension Width"
                        name="PrintServiceWidth"
                        onChange={(e) => {
                          handleInputChange(e, "_PrintService");
                          if (!values.PrintServiceWidth)
                            setValues({ ...values, PrintServiceUnits: "" });
                        }}
                      />
                      {values.PrintServiceWidth && (
                        <SelectControl
                          label="Units"
                          name="PrintServiceUnits"
                          value={
                            values.PrintServiceUnits
                              ? values.PrintServiceUnits
                              : ""
                          }
                          onChange={(e) =>
                            handleInputChange(e, "_PrintService")
                          }
                          options={[
                            { label: "cm", value: "cm" },
                            { label: "Inch", value: "Inch" },
                          ]}
                        />
                      )}
                    </Stack>
                    <InputControl
                      label="Tell Me More"
                      name="PrintServiceMoreInfo"
                      multiline
                      rows={3}
                      onChange={(e) => handleInputChange(e, "_PrintService")}
                    />
                  </ServiceCard>
                </Grid>
                <Grid item xs={12} md={6}>
                  <ServiceCard
                    services={[
                      "SignsOption",
                      "signsFile",
                      "SignsQuantity",
                      "SignsHeight",
                      "SignsWidth",
                      "SignsUnits",
                      "SignsMoreInfo",
                    ]}
                    values={values}
                    setValues={setValues}
                    switchName="_Signs"
                    icon="wi-signs"
                    title="Signs"
                  >
                    <RadioGroupControl
                      aria-label="signs"
                      name="SignsOption"
                      onChange={(e) => handleInputChange(e, "_Signs")}
                      options={[
                        {
                          label: "Design Request",
                          value: "Design Request",
                        },
                        {
                          label: "I Have a Design",
                          value: "I Have a Design",
                        },
                      ]}
                    />
                    <FormControl margin="normal" fullWidth>
                      <InputLabel>Upload Design / Example</InputLabel>
                      <FilesUploader
                        inputName="signsFile"
                        accept="image/*"
                        values={values}
                        setValues={setValues}
                      />
                    </FormControl>
                    <InputControl
                      type="number"
                      label="Quantity"
                      name="SignsQuantity"
                      onChange={(e) => handleInputChange(e, "_Signs")}
                    />
                    <Stack direction="row">
                      <InputControl
                        type="number"
                        label="Dimension Height"
                        name="SignsHeight"
                        onChange={(e) => handleInputChange(e, "_Signs")}
                      />
                      <InputControl
                        type="number"
                        label="Dimension Width"
                        name="SignsWidth"
                        onChange={(e) => {
                          handleInputChange(e, "_Signs");
                          if (!values.SignsWidth)
                            setValues({ ...values, SignsUnits: "" });
                        }}
                      />
                      {values.SignsWidth && (
                        <SelectControl
                          label="Units"
                          name="SignsUnits"
                          value={values.SignsUnits ? values.SignsUnits : ""}
                          onChange={(e) => handleInputChange(e, "_Signs")}
                          options={[
                            { label: "cm", value: "cm" },
                            { label: "Inch", value: "Inch" },
                          ]}
                        />
                      )}
                    </Stack>
                    <InputControl
                      label="Tell Me More"
                      name="SignsMoreInfo"
                      multiline
                      rows={3}
                      onChange={(e) => handleInputChange(e, "_Signs")}
                    />
                  </ServiceCard>
                </Grid>
                <Grid item xs={12} md={6}>
                  <ServiceCard
                    services={[
                      "FlagsAndBannersOption",
                      "flagsAndBannersFile",
                      "FlagsAndBannersQuantity",
                      "FlagsAndBannersHeight",
                      "FlagsAndBannersWidth",
                      "FlagsAndBannersUnits",
                      "FlagsAndBannersMoreInfo",
                    ]}
                    values={values}
                    setValues={setValues}
                    switchName="_FlagsAndBanners"
                    icon="wi-flags-banners"
                    title="Flags &amp; Banners"
                  >
                    <RadioGroupControl
                      aria-label="flags and banners"
                      name="FlagsAndBannersOption"
                      onChange={(e) => handleInputChange(e, "_FlagsAndBanners")}
                      options={[
                        {
                          label: "Design Request",
                          value: "Design Request",
                        },
                        {
                          label: "I Have a Design",
                          value: "I Have a Design",
                        },
                      ]}
                    />
                    <FormControl margin="normal" fullWidth>
                      <InputLabel>Upload Design / Example</InputLabel>
                      <FilesUploader
                        inputName="flagsAndBannersFile"
                        accept="image/*"
                        values={values}
                        setValues={setValues}
                      />
                    </FormControl>
                    <InputControl
                      type="number"
                      label="Quantity"
                      name="FlagsAndBannersQuantity"
                      onChange={(e) => handleInputChange(e, "_FlagsAndBanners")}
                    />
                    <Stack direction="row">
                      <InputControl
                        type="number"
                        label="Dimension Height"
                        name="FlagsAndBannersHeight"
                        onChange={(e) =>
                          handleInputChange(e, "_FlagsAndBanners")
                        }
                      />
                      <InputControl
                        type="number"
                        label="Dimension Width"
                        name="FlagsAndBannersWidth"
                        onChange={(e) => {
                          handleInputChange(e, "_FlagsAndBanners");
                          if (!values.FlagsAndBannersWidth)
                            setValues({ ...values, FlagsAndBannersUnits: "" });
                        }}
                      />
                      {values.FlagsAndBannersWidth && (
                        <SelectControl
                          label="Units"
                          name="FlagsAndBannersUnits"
                          value={
                            values.FlagsAndBannersUnits
                              ? values.FlagsAndBannersUnits
                              : ""
                          }
                          onChange={(e) =>
                            handleInputChange(e, "_FlagsAndBanners")
                          }
                          options={[
                            { label: "cm", value: "cm" },
                            { label: "Inch", value: "Inch" },
                          ]}
                        />
                      )}
                    </Stack>
                    <InputControl
                      label="Tell Me More"
                      name="FlagsAndBannersMoreInfo"
                      multiline
                      rows={3}
                      onChange={(e) => handleInputChange(e, "_FlagsAndBanners")}
                    />
                  </ServiceCard>
                </Grid>
                <Grid item xs={12} md={6}>
                  <ServiceCard
                    services={[
                      "ExhibitStandsOption",
                      "exhibitStandsFile",
                      "ExhibitStandsQuantity",
                      "ExhibitStandsHeight",
                      "ExhibitStandsWidth",
                      "ExhibitStandsUnits",
                      "ExhibitStandsMoreInfo",
                    ]}
                    values={values}
                    setValues={setValues}
                    switchName="_ExhibitStands"
                    icon="wi-exhibit-stands"
                    title="Exhibit Stands"
                  >
                    <RadioGroupControl
                      aria-label="exhibit stands"
                      name="ExhibitStandsOption"
                      onChange={(e) => handleInputChange(e, "_ExhibitStands")}
                      options={[
                        {
                          label: "Design Request",
                          value: "Design Request",
                        },
                        {
                          label: "I Have a Design",
                          value: "I Have a Design",
                        },
                      ]}
                    />
                    <FormControl margin="normal" fullWidth>
                      <InputLabel>Upload Design / Example</InputLabel>
                      <FilesUploader
                        inputName="exhibitStands"
                        accept="image/*"
                        values={values}
                        setValues={setValues}
                      />
                    </FormControl>
                    <InputControl
                      type="number"
                      label="Quantity"
                      name="ExhibitStandsQuantity"
                      onChange={(e) => handleInputChange(e, "_ExhibitStands")}
                    />
                    <Stack direction="row">
                      <InputControl
                        type="number"
                        label="Dimension Height"
                        name="ExhibitStandsHeight"
                        onChange={(e) => handleInputChange(e, "_ExhibitStands")}
                      />
                      <InputControl
                        type="number"
                        label="Dimension Width"
                        name="ExhibitStandsWidth"
                        onChange={(e) => {
                          handleInputChange(e, "_ExhibitStands");
                          if (!values.ExhibitStandsWidth)
                            setValues({ ...values, ExhibitStandsUnits: "" });
                        }}
                      />
                      {values.ExhibitStandsWidth && (
                        <SelectControl
                          label="Units"
                          name="ExhibitStandsUnits"
                          value={
                            values.ExhibitStandsUnits
                              ? values.ExhibitStandsUnits
                              : ""
                          }
                          onChange={(e) =>
                            handleInputChange(e, "_ExhibitStands")
                          }
                          options={[
                            { label: "cm", value: "cm" },
                            { label: "Inch", value: "Inch" },
                          ]}
                        />
                      )}
                    </Stack>
                    <InputControl
                      label="Tell Me More"
                      name="ExhibitStandsMoreInfo"
                      multiline
                      rows={3}
                      onChange={(e) => handleInputChange(e, "_ExhibitStands")}
                    />
                  </ServiceCard>
                </Grid>
                <Grid item xs={12} md={6}>
                  <ServiceCard
                    services={["OtherDetails"]}
                    values={values}
                    setValues={setValues}
                    switchName="_Other"
                    icon="wi-other"
                    title="Other"
                  >
                    <InputControl
                      label="Please Specify"
                      name="OtherDetails"
                      multiline
                      rows={3}
                      onChange={(e) => handleInputChange(e, "_Other")}
                    />
                  </ServiceCard>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={3} textAlign="center">
              <Fab
                aria-label="go back"
                onClick={() => navigate(-1)}
                sx={{ mb: 3 }}
              >
                <ArrowBackIcon />
              </Fab>
              <Typography color="text.secondary">
                Select services form left and click CONFIRM once done
              </Typography>
              <Divider variant="middle" sx={{ my: 2 }} />
              {Object.keys(values).some(
                (k) => /^[A-Z]/.test(k[0]) && values[k]
              ) ? (
                <ButtonControl onClick={nextStep} text="CONFIRM" />
              ) : (
                <ButtonControl text="CONFIRM" disabled />
              )}
            </Grid>
          </Grid>
        </Slide>

        <Slide
          direction="left"
          in={step === 1 ? true : false}
          style={{ display: step === 1 ? "flex" : "none" }}
        >
          <Grid container>
            <Grid item xs={12} md={9}>
              <Grid container spacing={1}>
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: "100%" }}>
                    <CardHeader
                      avatar={<InfoOutlinedIcon fontSize="large" />}
                      title="Other Info"
                    />
                    <Divider />
                    <CardContent>
                      <FormControl margin="normal" fullWidth>
                        <InputLabel>Upload Pictures</InputLabel>
                        <FilesUploader
                          inputName="infoFile"
                          accept="image/*"
                          values={values}
                          setValues={setValues}
                        />
                      </FormControl>
                      <InputControl
                        label="Any Other Necessary Info"
                        name="necessaryInfo_"
                        value={values.necessaryInfo_}
                        onChange={handleInputChange}
                        error={errors.necessaryInfo_}
                        multiline
                        rows={3}
                      />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormCustomerInfo
                    quoteId={quoteId}
                    values={values}
                    setValues={setValues}
                    handleInputChange={handleInputChange}
                    validate={validate}
                    errors={errors}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={3} textAlign="center">
              <Fab onClick={prevStep} aria-label="go back" sx={{ mb: 3 }}>
                <ArrowBackIcon />
              </Fab>
              <Typography color="text.secondary">Selected services</Typography>
              <List>
                {items.map((item) => (
                  <ListItem key={item}>
                    <ListItemIcon>
                      <CheckIcon color="success" />
                    </ListItemIcon>
                    <ListItemText
                      primary={item
                        .slice(1)
                        .replace(/([A-Z])/g, " $1")
                        .trim()}
                    />
                  </ListItem>
                ))}
              </List>
              <Divider variant="middle" sx={{ my: 2 }}>
                {fetchError}
              </Divider>
              {!isSaving ? (
                <ButtonControl
                  type="submit"
                  text="Submit"
                  isSaving={isSaving}
                />
              ) : (
                <ButtonControl
                  text="...Processing"
                  isSaving={isSaving}
                  disabled
                />
              )}
            </Grid>
          </Grid>
        </Slide>
      </form>

      <Slide
        direction="left"
        in={step === 2 ? true : false}
        style={{ display: step === 2 ? "flex" : "none" }}
      >
        <Grid container>
          <Grid item xs={12} md={9}>
            <Alert severity="success">Quote Created Successfully!</Alert>
          </Grid>
          <Grid item xs={12} md={3} px={5} textAlign="center">
            {responseId && (
              <ButtonControl
                text="Done"
                color="success"
                startIcon={<CheckIcon />}
                component={Link}
                to={`/job-details/${responseId.quoteId}`}
              />
            )}
          </Grid>
        </Grid>
      </Slide>
    </>
  );
}

export default memo(FormAdvertising);
